// Canonical tab lists for pages whose tabs are switched from more than one
// place (a page's own tab bar / URL routing in App.tsx, and the breadcrumb
// dropdown in Header.tsx). Each list is the single source of truth for its
// page's valid tab values, display labels, and (where the URL segment isn't
// just the value) its URL slug — import from here instead of re-typing the
// list at each call site.

export interface TabDef<V extends string = string> {
  value: V;
  label: string;
  slug?: string;
}

// The 'purchases' tab is reachable but not offered in the breadcrumb
// dropdown (see BUDGET_CATEGORY_DROPDOWN_TABS) — kept here so the label
// lookup used for the breadcrumb crumb text still covers it.
export type BudgetCategoryTab = 'overview' | 'vendors' | 'purchases' | 'payments';
export const BUDGET_CATEGORY_TABS: TabDef<BudgetCategoryTab>[] = [
  { value: 'vendors', label: 'Overview' },
  { value: 'overview', label: 'Details' },
  { value: 'payments', label: 'Payments' },
  { value: 'purchases', label: 'Purchases' },
];
export const BUDGET_CATEGORY_DROPDOWN_TABS: TabDef<BudgetCategoryTab>[] =
  BUDGET_CATEGORY_TABS.filter(t => t.value !== 'purchases');

export type PaymentsTab = 'payments' | 'contributors' | 'settlement';
export const PAYMENTS_TABS: TabDef<PaymentsTab>[] = [
  { value: 'payments', label: 'Overview' },
  { value: 'contributors', label: 'Contributors & Spending' },
  { value: 'settlement', label: 'Settlement Reconciliation' },
];

export type LogisticsTab = 'vehicles' | 'assignments' | 'drivers';
export const LOGISTICS_TABS: TabDef<LogisticsTab>[] = [
  { value: 'vehicles', label: 'Vehicles', slug: 'vehicles' },
  { value: 'assignments', label: 'Schedule', slug: 'schedule' },
  { value: 'drivers', label: 'Drivers', slug: 'drivers' },
];

export type EventDayTab = 'overview' | 'checkin' | 'seating' | 'waiter' | 'kitchen' | 'bar' | 'favours';
export const EVENT_DAY_TABS: TabDef<EventDayTab>[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'checkin', label: 'Guest Check-In' },
  { value: 'seating', label: 'Seating' },
  { value: 'waiter', label: 'Waiter' },
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'bar', label: 'Bar' },
  { value: 'favours', label: 'Favours' },
];

export function tabLabel<V extends string>(tabs: TabDef<V>[], value: V | undefined, fallback: string): string {
  return tabs.find(t => t.value === value)?.label ?? fallback;
}

export function slugForTab<V extends string>(tabs: TabDef<V>[], value: V): string {
  return tabs.find(t => t.value === value)?.slug ?? value;
}

export function tabForSlug<V extends string>(tabs: TabDef<V>[], slug: string, fallback: V): V {
  return tabs.find(t => (t.slug ?? t.value) === slug)?.value ?? fallback;
}
